import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, formatErr } from "@/lib/auth";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import GoogleButton from "@/components/GoogleButton";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, RefreshCw } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [mfa, setMfa] = useState(null); // { mfa_token } once step-1 clears
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMode, setMfaMode] = useState("totp"); // "totp" | "recovery"
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyGate, setVerifyGate] = useState(null); // { email, message } when 403 email_not_verified
  const [resending, setResending] = useState(false);
  const { login, loginMfa, loginMfaRecovery } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");

  const finish = (u) => {
    if (redirect && redirect.startsWith("/")) { nav(redirect); return; }
    // Staff without MFA are hard-bounced to /mfa/setup — the account is
    // gated until they enrol an authenticator.
    if (u.mfa_setup_required) { nav("/mfa/setup"); return; }
    // Fresh patients who verified but never finished the 60-second onboarding
    // are routed there once — after that, straight to /app on every login.
    if (u.role === "patient" && !u.onboarded_at) { nav("/onboarding"); return; }
    nav(u.role === "patient" ? "/app" : u.role === "admin" ? "/admin" : "/clinician");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setVerifyGate(null); setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.mfa_required) { setMfa({ mfa_token: u.mfa_token }); return; }
      finish(u);
    } catch (e) {
      // 403 with structured detail = email verification gate. Show the
      // "check your inbox" screen with a Resend button instead of a raw error.
      const d = e.response?.data?.detail;
      if (e.response?.status === 403 && d && typeof d === "object" && d.code === "email_not_verified") {
        setVerifyGate({ email: d.email || email, message: d.message });
      } else {
        setErr(formatErr(d) || e.message);
      }
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    if (!verifyGate?.email) return;
    setResending(true);
    try {
      await api.post("/security/email/resend-public", { email: verifyGate.email });
      toast.success("Verification email sent — check your inbox");
    } catch { toast.error("Could not send — try again shortly"); }
    finally { setResending(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = mfaMode === "recovery"
        ? await loginMfaRecovery(mfa.mfa_token, mfaCode.trim())
        : await loginMfa(mfa.mfa_token, mfaCode.trim());
      finish(u);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="absolute top-6 right-6 flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
      <div className="w-full max-w-md">
        <Link to="/" className="mb-10 flex justify-center">
          <Logo variant="wordmark" size="lg" />
        </Link>
        <div className="fl-card p-8">
          <h1 className="fl-heading text-3xl">{t("auth.welcome")}</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>{t("auth.welcomeSub")}</p>

          {verifyGate ? (
            <div className="mt-6 space-y-4" data-testid="login-verify-gate">
              <div className="w-12 h-12 rounded-full bg-[#f47a20]/15 border border-[#f47a20]/40 flex items-center justify-center mx-auto">
                <Mail size={20} className="text-[#f47a20]" />
              </div>
              <div className="text-center">
                <div className="fl-heading text-xl">Check your inbox</div>
                <p className="text-sm text-zinc-400 mt-2">
                  {verifyGate.message || "We sent a verification link to your email. Click it to activate your account, then come back and sign in."}
                </p>
                <div className="text-xs text-zinc-500 mt-3">Sent to <span className="text-zinc-300">{verifyGate.email}</span></div>
              </div>
              <button
                onClick={resendVerification}
                disabled={resending}
                className="fl-btn w-full flex items-center justify-center gap-2"
                data-testid="login-verify-resend"
              >
                <RefreshCw size={13} className={resending ? "animate-spin" : ""} />
                {resending ? "Sending…" : "Resend verification email"}
              </button>
              <button
                onClick={() => setVerifyGate(null)}
                className="text-xs text-zinc-500 hover:text-[#f47a20] w-full"
                data-testid="login-verify-back"
              >
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          <div className="mt-6"><GoogleButton testId="login-google" /></div>
          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider" style={{ color: "var(--fl-muted-2)" }}>
            <div className="flex-1 h-px" style={{ background: "var(--fl-border)" }} /> {t("auth.orEmail")} <div className="flex-1 h-px" style={{ background: "var(--fl-border)" }} />
          </div>

          {!mfa ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="fl-label mb-2">{t("auth.email")}</div>
                <input className="fl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              </div>
              <div>
                <div className="fl-label mb-2">{t("auth.password")}</div>
                <div className="relative">
                  <input
                    className="fl-input pr-11"
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    title={showPw ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-500 hover:text-[#f47a20] transition-colors"
                    data-testid="login-password-toggle"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
              <button className="fl-btn w-full justify-center" disabled={loading} data-testid="login-submit">{loading ? t("common.loading") : t("auth.signInBtn")}</button>
            </form>
          ) : (
            <form onSubmit={submitMfa} className="space-y-4" data-testid="login-mfa-form">
              <div className="fl-label text-[#f47a20]">
                {mfaMode === "recovery" ? "Recovery code" : "Two-factor authentication"}
              </div>
              <p className="text-sm text-zinc-400">
                {mfaMode === "recovery"
                  ? "Lost access to your authenticator? Paste one of the single-use recovery codes you saved during setup."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
              {mfaMode === "recovery" ? (
                <input
                  className="fl-input tracking-[0.35em] text-center text-base uppercase"
                  inputMode="text"
                  maxLength={14}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  placeholder="XXXX-XXXX-XXXX"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                  data-testid="login-mfa-recovery-code"
                />
              ) : (
                <input
                  className="fl-input tracking-[0.6em] text-center text-lg"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="login-mfa-code"
                />
              )}
              {err && <div className="text-sm text-red-400" data-testid="login-mfa-error">{err}</div>}
              <button className="fl-btn w-full justify-center"
                disabled={loading || (mfaMode === "totp" ? mfaCode.length !== 6 : mfaCode.replace(/-/g, "").length < 10)}
                data-testid="login-mfa-submit">
                {loading ? t("common.loading") : "Verify & sign in"}
              </button>
              <button
                type="button"
                className="text-xs text-zinc-400 hover:text-[#f47a20] w-full"
                onClick={() => { setMfaMode(mfaMode === "recovery" ? "totp" : "recovery"); setMfaCode(""); setErr(""); }}
                data-testid="login-mfa-toggle-mode"
              >
                {mfaMode === "recovery" ? "← Back to authenticator code" : "Use a recovery code instead"}
              </button>
              <button type="button" className="text-xs text-zinc-500 hover:text-[#f47a20] w-full" onClick={() => { setMfa(null); setMfaCode(""); setMfaMode("totp"); setErr(""); }} data-testid="login-mfa-cancel">
                ← Use a different account
              </button>
            </form>
          )}
          </>
          )}
          <div className="mt-6 text-sm text-zinc-400 text-center">
            {t("auth.noAccount")} <Link to={redirect ? `/register?redirect=${encodeURIComponent(redirect)}` : "/register"} className="text-[#f47a20]">{t("auth.createOne")}</Link>
            <div className="mt-2"><Link to="/forgot-password" className="text-zinc-500 hover:text-[#f47a20] text-xs" data-testid="login-forgot">{t("auth.forgotPw")}</Link></div>
            <div className="mt-3 text-[10px] text-zinc-600">
              <Link to="/privacy" className="hover:text-[#f47a20]" data-testid="login-privacy">Privacy Policy</Link>
            </div>
            <div className="mt-2"><Link to="/trust" className="text-zinc-500 hover:text-[#f47a20] text-xs" data-testid="login-trust-link">Trust & Data Safety</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
